import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import SiteFooter from "../components/SiteFooter";
import { getBrand, pubFromHostname } from "../lib/brand";

const H2 = ({ children }) => (
  <h2 className="text-lg font-bold mt-10 mb-3">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-sm text-black leading-relaxed mb-4">{children}</p>
);
const LI = ({ children }) => (
  <li className="text-sm text-black leading-relaxed mb-2">{children}</li>
);

const SERVICES_TABLE = [
  ["Service", "What they do for us", "Where data may be held"],
  ["GoHighLevel", "Contact records and emails", "United States"],
  ["Google", "File storage and draft documents", "United States and other countries"],
  ["Resend", "Sending emails", "United States"],
  ["Writing and editing tools", "Help drafting and polishing features", "United States"],
];

const COOKIES_TABLE = [
  ["What", "Why", "How long"],
  ["Sign-in", "Keeps you signed in so you can come back to your submission", "Until you sign out or it expires"],
  ["Your progress", "Saves your answers as you go", "Until you submit"],
  ["Security", "Protects the app from misuse", "Your visit"],
];

function Table({ rows, testid }) {
  return (
    <table className="w-full text-sm mb-6 border border-[#c19872] rounded-lg overflow-hidden" data-testid={testid}>
      <thead>
        <tr className="bg-[#f5f3ed] border-b border-[#c19872]">
          {rows[0].map((h) => (
            <th key={h} className="text-left px-3 py-2 font-bold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(1).map((r, i) => (
          <tr key={i} className="border-b border-[#dcd3c6] last:border-b-0">
            {r.map((c, j) => <td key={j} className="px-3 py-2">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Privacy() {
  return (
    <>
      <p className="text-xs text-black mb-6">Last updated: 1 September 2026</p>
      <P>Sharing your love story is a big deal. So is trusting us with it. Here's what we collect, why we collect it, and what you can ask us to do with it.</P>

      <H2>1. Who we are</H2>
      <P>Dancing With Her® and Dancing With Them® are independent publications based in Bendigo, Victoria, Australia. Both are registered business names of the same sole trader, ABN 21 440 059 775. When we say "we" or "us", that's who we mean.</P>
      <P>This policy covers Behind the Love, the app where lovers and wedding pros send us stories to feature.</P>
      <P>Questions? Email <a className="underline" href="mailto:editor@dancingwithher.com">editor@dancingwithher.com</a> or <a className="underline" href="mailto:editor@dancingwiththem.com">editor@dancingwiththem.com</a>.</P>

      <H2>2. What we collect</H2>
      <ul className="list-disc pl-5 mb-4">
        <LI><strong>From lovers:</strong> your names and email addresses, event details (date, location, type), your answers to our story questions, the photos and links you share, the vendors you worked with, your publication preference and the consents you give.</LI>
        <LI><strong>From wedding pros:</strong> your business name, contact name, email, website, social handles and service category.</LI>
        <LI><strong>The personal stuff:</strong> love stories are personal. Yours might tell us about your sexuality, gender identity, relationship, culture, faith or health. The law treats this as sensitive information, and so do we. We only collect it with your clear consent, and only use it to tell your story.</LI>
        <LI><strong>Behind the scenes:</strong> sign-in details stored in your browser, and basic server logs (like IP address and browser type) that keep the app secure.</LI>
      </ul>
      <P>We don't take payments in this app.</P>

      <H2>3. How we use it</H2>
      <ul className="list-disc pl-5 mb-4">
        <LI>To read your submission, shape your feature and publish it on our websites, in our print magazine, in our newsletters and on our socials.</LI>
        <LI>To keep in touch about your submission.</LI>
        <LI>To credit your photographer and vendors. They deserve the shout-out.</LI>
        <LI>To send you our newsletter, but only if you tick the box. Unsubscribe any time.</LI>
        <LI>To keep the app secure and keep a record of what we've published.</LI>
      </ul>
      <P>If you're in the UK or EU, our legal bases are your consent (for sensitive information and the newsletter), taking the steps you've asked us to take with your submission, and our legitimate interests (crediting vendors, security and records).</P>

      <H2>4. How we put your feature together</H2>
      <P>Our editors shape every feature. Along the way, we may use writing and editing tools, including AI-assisted ones, to help draft and polish your story. A person always reviews your feature before it goes live, and a person always decides whether your story is accepted.</P>

      <H2>5. Who we share it with</H2>
      <P>We only share your information with the services that help us run the app, and with the world once your feature is published.</P>
      <Table rows={SERVICES_TABLE} testid="privacy-services-table" />
      <P>When your feature goes live, your names, photos and story are public. That's the whole point! We may let the vendors in your story know they've been featured. We never sell your personal information.</P>

      <H2>6. Storage outside Australia</H2>
      <P>Some of these services store data outside Australia, mostly in the United States. We choose services with proper safeguards and, where the law requires it, rely on their standard contractual clauses or equivalent protections.</P>

      <H2>7. How long we keep it</H2>
      <ul className="list-disc pl-5 mb-4">
        <LI>Submissions that are declined or never finished: deleted 12 months after being declined or last edited.</LI>
        <LI>Published features: kept while the feature is live, plus the records we need of what we published.</LI>
        <LI>Newsletter details: until you unsubscribe.</LI>
      </ul>

      <H2>8. You're in control</H2>
      <P>You can ask us to see, correct or delete your information, take back a consent, or remove your published feature from our websites. Just email us and we'll reply within 30 days.</P>
      <P>One honest catch: we can't un-print a magazine, or pull back copies other people have already shared.</P>
      <P>If you're in the UK or EU, you can also object to how we use your data, ask us to restrict it, or ask for a copy.</P>
      <P>Not happy with our answer? We'd love the chance to put it right, but you can also complain to the Office of the Australian Information Commissioner (oaic.gov.au), the UK Information Commissioner's Office (ico.org.uk) or your local data protection authority.</P>

      <H2>9. Keeping it safe</H2>
      <P>The app uses encrypted connections (HTTPS), and only our team can sign in to the admin area. No system is completely secure, so tell us straight away if you think your account has been misused.</P>

      <H2>10. Age</H2>
      <P>You need to be 18 or over to submit. Photos can include children only with permission from their parent or guardian.</P>

      <H2>11. Changes</H2>
      <P>If anything changes, we'll update this page and the date at the top.</P>
    </>
  );
}

function Terms() {
  return (
    <>
      <p className="text-xs text-black mb-6">Last updated: 1 September 2026</p>
      <P>The short version: you tell us your story and you keep ownership of it. We get permission to share it beautifully, with credit where it's due. Everyone in it needs to be on board, and you can ask us to take it down any time. The full terms below are the ones that count.</P>

      <H2>1. About these terms</H2>
      <P>These terms apply when you send a story to Dancing With Her or Dancing With Them through this app. By submitting, you agree to them and to our Privacy Policy. You need to be 18 or over.</P>

      <H2>2. What we're looking for</H2>
      <P>Real weddings, elopements, engagements, love stories and features we've invited. Every story must be true. Styled shoots must star real-life partners, not models.</P>

      <H2>3. Your promises to us</H2>
      <P>When you submit, you're confirming that:</P>
      <ul className="list-disc pl-5 mb-4">
        <LI>Everyone named as a lover in the submission agrees to it being sent and published.</LI>
        <LI>You own the photos and content you send, or have permission to send them. For professional photos, that means your photographer's permission.</LI>
        <LI>Anyone clearly identifiable in the photos agrees to them being published, and a parent or guardian has agreed for any child.</LI>
        <LI>The vendor credits you give us are accurate.</LI>
        <LI>Nothing in your submission breaks anyone else's rights or the law.</LI>
      </ul>

      <H2>4. You keep ownership</H2>
      <P>You (or your photographer) keep the copyright in everything you send us. You give us a worldwide, non-exclusive, royalty-free licence to use, edit, crop, format and publish your submission on our websites, in our print magazine, in our newsletters and on our socials, and to promote your feature. The licence continues after publication. It also covers the services and print partners who help us produce your feature, and anyone who takes over our publications in future.</P>
      <P>Non-exclusive means you're free to share your story anywhere else, whenever you like.</P>

      <H2>5. Credit where it's due</H2>
      <P>We always credit your photographer, and we credit the vendors you name wherever we can.</P>

      <H2>6. Our editorial calls</H2>
      <P>We'd love to feature every story, but we can't. We decide what to publish, when, where and how, and we may edit for length, clarity and style. We may use writing and editing tools to help shape your feature, and a person always reviews it before it goes live. It's free to submit, and we don't pay for submissions.</P>

      <H2>7. Changed your mind?</H2>
      <P>Just email <a className="underline" href="mailto:editor@dancingwithher.com">editor@dancingwithher.com</a> or <a className="underline" href="mailto:editor@dancingwiththem.com">editor@dancingwiththem.com</a> and we'll remove your feature from our websites within 30 days. We can't un-print a magazine, or pull back copies other people have already shared.</P>

      <H2>8. Partner vendors</H2>
      <P>Some vendors pay to partner with us, and partners may get priority publishing. We're open about it: partners are labelled as Dancing With Her partners or Dancing With Them partners in the vendor list on every feature.</P>

      <H2>9. The legal bits</H2>
      <P>We provide this app as it is. As far as the law allows, we're not responsible for indirect loss from using it. Nothing in these terms takes away rights you have under the Australian Consumer Law, or any other law that can't be excluded.</P>

      <H2>10. Governing law</H2>
      <P>These terms are governed by the laws of Victoria, Australia. That doesn't take away any consumer rights you have where you live.</P>

      <H2>11. Contact us</H2>
      <P>Dancing With Her® and Dancing With Them®, ABN 21 440 059 775, Parcel Locker 10, Bendigo VIC 3550, Australia. Email <a className="underline" href="mailto:editor@dancingwithher.com">editor@dancingwithher.com</a> or <a className="underline" href="mailto:editor@dancingwiththem.com">editor@dancingwiththem.com</a>.</P>
      <P>Dancing With Her® and Dancing With Them® are registered trademarks.</P>
    </>
  );
}

function Cookies() {
  return (
    <>
      <P>No cookie banner here, and that's on purpose. This app only uses the browser storage it needs to work:</P>
      <Table rows={COOKIES_TABLE} testid="cookies-table" />
      <P>No analytics, no ads, no tracking, so there's nothing to accept or decline. If that ever changes, we'll ask you first.</P>
      <P>Our main websites, dancingwithher.com and dancingwiththem.com, are covered by their own privacy policies.</P>
    </>
  );
}

const PAGES = {
  privacy: { title: "Privacy Policy", overline: "Legal", Body: Privacy },
  terms: { title: "Submission Terms", overline: "Legal", Body: Terms },
  cookies: { title: "Cookie notice", overline: "Legal", Body: Cookies },
};

export default function LegalPage({ kind }) {
  const [params] = useSearchParams();
  const [pub, setPub] = useState(null);
  useEffect(() => {
    (async () => {
      const brand = await getBrand();
      const qp = params.get("pub");
      setPub(qp === "dwh" || qp === "dwt" ? qp : pubFromHostname(brand));
    })();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (pub) document.documentElement.dataset.pub = pub;
    return () => { delete document.documentElement.dataset.pub; };
  }, [pub]);

  const page = PAGES[kind] || PAGES.privacy;
  return (
    <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid={`legal-${kind}`} data-pub={pub || "neutral"}>
      <PublicHeader pub={pub} />
      <main className="w-full max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-16 flex-1">
        <p className="btw-step-label">{page.overline}</p>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-6">{page.title}</h1>
        <div className="bg-white border border-[#c19872] rounded-xl p-6 sm:p-10">
          <page.Body />
        </div>
      </main>
      <SiteFooter pub={pub} />
    </div>
  );
}
